/**
 * Dashboard 순수 유틸리티 함수 & EmptyDash 컴포넌트
 */

import React from 'react';
import type { Influencer, Campaign, FilterState, InfluencerStatus } from './types';
import {
  CAMPAIGN_INFLUENCER_STATUSES,
  FILTERABLE_CHANNEL_PLATFORM_ENTRIES,
} from './constants';
import { smallNumber } from '@/lib/utils/misc';

/* ──────────────────── 빈 셀 표시 컴포넌트 ──────────────────── */

/** 빈 데이터 표시 컴포넌트 — 스타일/문자 변경 시 여기만 수정 */
export function EmptyDash(): React.ReactElement {
  return React.createElement('span', { className: 'empty-dash' }, '—');
}

/* ──────────────────── 이메일 ──────────────────── */

/**
 * Resolve an influencer's full email list. The new admin API omits the aggregate
 * `allEmails`, so we rebuild it from the individual fields (mirroring the legacy
 * `emailsArray` virtual: registration + contact + selfProfile, deduped). Also
 * accepts a string[] or a legacy comma-joined string when a BE path provides one.
 */
export function getInfluencerEmails(user: {
  email?: string;
  contactEmail?: string;
  selfProfile?: { email?: string };
  allEmails?: string[] | string;
}): string[] {
  const all = user.allEmails;
  if (Array.isArray(all) && all.length > 0) return all.filter(Boolean);
  if (typeof all === 'string' && all.trim()) {
    return all.split(',').map((e) => e.trim()).filter(Boolean);
  }
  return Array.from(new Set(
    [user.email, user.contactEmail, user.selfProfile?.email].filter(Boolean) as string[],
  ));
}

/** Emails that should be treated as "no real contact" (mirrors InfluencerRow's old logic). */
const INCORRECT_EMAILS = new Set([
  'nocontact@gamechanger.ai',
  'nocontact@ggcontent.com',
  'noreply@youtube.com',
]);

export const isIncorrectEmail = (e: string): boolean =>
  INCORRECT_EMAILS.has(e) || /@pages\.plusgoogle\.com/.test(e);

/**
 * Sentinel placeholder used by "No Contact" / "Unknown email?" shortcuts to mark that an
 * influencer has no reachable email. Must stay in INCORRECT_EMAILS above so the placeholder
 * is recognized as a bad email everywhere. Mirrors EditChannelDialog's local
 * NO_CONTACT_EMAIL constant of the same value — keep them in sync.
 */
export const NO_CONTACT_EMAIL = 'nocontact@ggcontent.com';

/* ──────────────────── 숫자 포맷 ──────────────────── */

export function formatNum(num?: number): string {
  if (!num) return '0';
  return num.toLocaleString('en-US');
}

/** 소액 단가 포맷의 loosely-typed 래퍼. 포맷 규칙 자체는 공용 `smallNumber` 하나만
 *  쓰므로($/click·$/view 등이 페이지마다 다르게 보이지 않도록), 여기서는 API에서 온
 *  `unknown` 값을 숫자로 좁히는 역할만 한다. */
export function smallNum(v: unknown): string {
  return smallNumber(typeof v === 'number' ? v : null);
}

/** 정수 포맷 (콤마 구분, 반올림) */
export function humanizeNum(v: unknown): string {
  if (v == null || typeof v !== 'number' || isNaN(v)) return '-';
  return Math.round(v).toLocaleString();
}

/* ──────────────────── 인플루언서 상태 ──────────────────── */

export function getAllowedStatuses(isAdmin: boolean): InfluencerStatus[] {
  if (isAdmin) return CAMPAIGN_INFLUENCER_STATUSES;
  return CAMPAIGN_INFLUENCER_STATUSES.filter((s) => !s.requiresToAccessAllFeatures);
}

/* ──────────────────── 파일 다운로드 헬퍼 ──────────────────── */

/** 새 탭을 열지 않고 파일을 직접 다운로드합니다. */
export function triggerDownload(url: string, filename?: string): void {
  const a = document.createElement('a');
  a.href = url;
  if (filename) a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * Fetches `url` and, only if the response looks like a real file, triggers a client-side
 * download from a `blob:` URL. Returns `false` (without downloading anything) on a network
 * error, a non-2xx response, or an HTML response — the last case covers a dead/legacy API
 * route where the SPA's own index.html gets served back instead of a file, which an `<a
 * href>` click would otherwise silently navigate to (full page load, no download).
 *
 * `filenameOverride`, when given, wins over the server's `Content-Disposition` filename —
 * used to fold client-side-only context (e.g. the active status filter) into the saved
 * filename that the BE has no way to know about.
 */
export async function fetchAndDownload(url: string, filenameOverride?: string): Promise<boolean> {
  let res: Response;
  try {
    res = await fetch(url, { credentials: 'include' });
  } catch {
    return false;
  }
  const contentType = res.headers.get('content-type') ?? '';
  if (!res.ok || contentType.includes('text/html')) return false;
  const disposition = res.headers.get('content-disposition') ?? '';
  const filenameMatch = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const filename = filenameOverride ?? (filenameMatch ? decodeURIComponent(filenameMatch[1]) : undefined);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  triggerDownload(blobUrl, filename);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  return true;
}

/* ──────────────────── 가격 추정 ──────────────────── */

/** Most recent completed-campaign final price for this org (원본 EstPriceCell/calculateProposalPrice). */
export function getProposedPrice(user: Influencer, orgId?: string): number | null {
  const camps = user.campaigns;
  if (!camps?.length) return null;
  const completed = camps
    .filter((c) => c.status === 'completed' && (c.priceFinal != null || c.finalPrice != null) && (!orgId || String(c.organizationId) === String(orgId)))
    .sort((a, b) => (new Date(b.createdAt || 0).getTime()) - (new Date(a.createdAt || 0).getTime()));
  const last = completed[0];
  return last ? (last.finalPrice ?? last.priceFinal ?? null) : null;
}

/** campaign.expectedPricePerView × recent views (원본 EstPriceCell/calculateEstimatedPrice). */
export function getPpvEstimate(user: Influencer, campaign?: Campaign | null): number | null {
  const ppv = campaign?.expectedPricePerView;
  const views = user.stats?.last5?.views ?? user.averageStats?.last5?.avgViews;
  if (!ppv || !views) return null;
  const price = ppv * views;
  return price > 0 ? price : null;
}

/* ──────────────────── 행 스트라이프 ──────────────────── */

/**
 * 노란 줄무늬: 최근 1개월 이상 활동 없고 stats도 오래된 경우
 * 회색 줄무늬: 최근 1개월 이상 활동 없지만 stats는 최근에 업데이트된 경우
 */
const INACTIVE_CONFIG: Record<string, { threshold: number; getActivityDate: (u: Influencer) => string | undefined }> = {
  youtube: { threshold: 30, getActivityDate: (u) => u.latestPost?.publishedAt },
  twitch:  { threshold: 30, getActivityDate: (u) => u.twitchStats?.lastStreamAt },
  twitter: { threshold: 30, getActivityDate: (u) => u.latestPost?.publishedAt },
};

export function getRowStripeClass(user: Influencer): string {
  const channelType = user.channels?.[0]?.type;
  if (!channelType) return '';
  const config = INACTIVE_CONFIG[channelType];
  if (!config) return '';
  const activityDate = config.getActivityDate(user);
  if (!activityDate) return '';
  const thresholdDate = new Date(Date.now() - config.threshold * 24 * 60 * 60 * 1000);
  if (new Date(activityDate) >= thresholdDate) return '';
  if (user.statsUpdatedAtStatus === 'ok' && user.statsUpdatedAt && new Date(user.statsUpdatedAt) > thresholdDate) {
    return 'stripe-gray';
  }
  return 'stripe-yellow';
}

/* ──────────────────── 인플루언서 검색 쿼리 파라미터 ──────────────────── */

/**
 * Maps a FilterState (+ campaign context) into the API query params accepted by
 * `getUsers` / `getUsersTotalPage`. Shared by the main dashboard's `loadInfluencers`
 * and the "add influencers" search drawer so both build identical queries.
 * When `campaignId === 'all'`, no campaignId is sent (system-wide search).
 */
export function buildUsersQueryParams(params: FilterState, campaignId: string): Record<string, unknown> {
  const apiParams: Record<string, unknown> = { page: params.page };
  if (params.channelName) apiParams.channelName = params.channelName;
  if (params.channelId) apiParams.channelId = params.channelId;
  if (params.email) apiParams.email = params.email;
  if (params.country.length > 0) apiParams.country = params.country;
  if (params.language.length > 0) apiParams.language = params.language;
  if (params.subscriberCount) apiParams.subscriberCount = params.subscriberCount;
  if (params.keywords) apiParams.keywords = params.keywords;
  if (params.tags) apiParams.tags = params.tags;
  if (params.categories) apiParams.categories = params.categories;
  if (params.inclusionComment) apiParams.inclusionComment = params.inclusionComment;
  if (params.recentGame) apiParams.recentGame = params.recentGame;
  if (params.recentGenre) apiParams.recentGenre = params.recentGenre;
  if (params.offer) apiParams.offer = params.offer;
  if (params.sortBy) apiParams.sortBy = params.sortBy;
  if (params.isRegistered && params.isRegistered !== 'any') apiParams.isRegistered = params.isRegistered;
  if (params.hasRates && params.hasRates !== 'any') apiParams.hasRates = params.hasRates;
  if (params.hasDemographic && params.hasDemographic !== 'any') apiParams.hasDemographic = params.hasDemographic;
  if (params.hasStreams && params.hasStreams !== 'any') apiParams.hasStreams = params.hasStreams;
  if (params.youtubeWatchLiveStreams && params.youtubeWatchLiveStreams !== 'any') apiParams.youtubeWatchLiveStreams = params.youtubeWatchLiveStreams;
  if (params.updateTiktokSocialStats && params.updateTiktokSocialStats !== 'any') apiParams.updateTiktokSocialStats = params.updateTiktokSocialStats;
  if (params.updateTiktokChannelStats && params.updateTiktokChannelStats !== 'any') apiParams.updateTiktokChannelStats = params.updateTiktokChannelStats;
  if (params.updateDouyinSocialStats && params.updateDouyinSocialStats !== 'any') apiParams.updateDouyinSocialStats = params.updateDouyinSocialStats;
  if (params.updateDouyinChannelStats && params.updateDouyinChannelStats !== 'any') apiParams.updateDouyinChannelStats = params.updateDouyinChannelStats;
  if (params.updateFacebookChannelStats && params.updateFacebookChannelStats !== 'any') apiParams.updateFacebookChannelStats = params.updateFacebookChannelStats;
  if (params.hasCampaignHistory === 'yes') {
    apiParams.genuineCampaignStatus = CAMPAIGN_INFLUENCER_STATUSES.map((s) => s.value);
  }
  const effectiveCampaignId = campaignId !== 'all' ? campaignId : (params.filterByCampaignId || '');
  if (effectiveCampaignId) apiParams.campaignId = effectiveCampaignId;
  if (params.status && params.status.length > 0) apiParams.status = params.status;
  if (params.candidateGroupName) apiParams.candidateGroupName = params.candidateGroupName;

  const activeTypes = FILTERABLE_CHANNEL_PLATFORM_ENTRIES
    .filter(({ filterKey }) => params[filterKey as keyof FilterState])
    .map(({ type }) => type);
  if (activeTypes.length > 0 && activeTypes.length < FILTERABLE_CHANNEL_PLATFORM_ENTRIES.length) {
    apiParams.channelTypes = activeTypes;
  }
  return apiParams;
}

/* ──────────────────── 클라이언트측 추가 필터 ────────────────────
   캠페인 상세(campaign mode)에서 인포바의 "추가 필터" 팝오버 조건.
   신규 v2 BE(admin/campaign/users/get)는 ES 미구현으로 channelName·country 등
   검색 조건을 서버측에서 무시하므로(placeholder findUsers), 이 조건들은 이미
   로드된 전체 로스터를 클라이언트에서 필터링한다. 매칭 규칙은 원본
   gc-mono packages/common/userFilter.js 의 getUsersFilter 규칙을 따른다. */

/** All client-side filter conditions. channelName/tags are exposed as quick
 *  inputs in the infobar by default; every field is editable in the Filters popover. */
export interface ClientFilters {
  channelName: string;
  tags: string;
  country: string;
  language: string;
  subscriberCount: string; // "min~max"
  recentGame: string;
  keywords: string;
  categories: string;
  recentGenre: string;
  email: string;
  inclusionComment: string;
}

export type ClientFilterKey = keyof ClientFilters;

/** Display order of the filter fields (infobar quick inputs & popover rows). */
export const CLIENT_FILTER_KEYS: ClientFilterKey[] = [
  'channelName', 'tags', 'country', 'language', 'subscriberCount',
  'recentGame', 'keywords', 'categories', 'recentGenre', 'email', 'inclusionComment',
];

/** Fields only shown to admins (mirrors the sidebar Search Conditions gating). */
export const ADMIN_ONLY_FILTER_KEYS: ReadonlySet<ClientFilterKey> = new Set<ClientFilterKey>([
  'categories', 'recentGenre', 'email', 'inclusionComment',
]);

/** Default fields exposed as infobar quick inputs. */
export const DEFAULT_QUICK_FILTER_KEYS: ClientFilterKey[] = ['channelName', 'tags'];

/** Max number of fields that can be exposed as infobar quick inputs at once. */
export const MAX_QUICK_FILTER_KEYS = 3;

export const EMPTY_CLIENT_FILTERS: ClientFilters = {
  channelName: '',
  tags: '',
  country: '',
  language: '',
  subscriberCount: '',
  recentGame: '',
  keywords: '',
  categories: '',
  recentGenre: '',
  email: '',
  inclusionComment: '',
};

/** True when at least one client filter field has a non-empty value. */
export function hasActiveClientFilters(f: ClientFilters): boolean {
  return Object.values(f).some((v) => v.trim() !== '');
}

/** Number of active client filter fields (for the button badge). */
export function countActiveClientFilters(f: ClientFilters): number {
  return Object.values(f).filter((v) => v.trim() !== '').length;
}

/** Parse a "min~max" range string into [from, to] (null = open-ended). Mirrors userFilter.js sanitizeRange. */
function parseRange(raw: string): [number | null, number | null] {
  const parts = raw.split('~').map((s) => s.trim());
  const toNum = (s: string | undefined): number | null => {
    if (s == null || s === '') return null;
    const n = Number(s.replace(/,/g, ''));
    return Number.isNaN(n) ? null : n;
  };
  return [toNum(parts[0]), toNum(parts[1])];
}

/** Parse a "title, NN%" recentGame string → { title, percent }. Mirrors userFilter.js recentGame rule. */
export function parseTitlePercent(raw: string): { title: string; percent: number | null } {
  const m = raw.trim().match(/^(.+?)[,\s]+(\d+)\s*%$/);
  if (m) return { title: m[1].trim(), percent: Number(m[2]) };
  return { title: raw.trim(), percent: null };
}

export const titleOf = (item: string | { title?: string; percent?: number }): string =>
  typeof item === 'string' ? item : item?.title ?? '';
export const percentOf = (item: string | { title?: string; percent?: number }): number =>
  typeof item === 'object' && item?.percent != null ? item.percent : 0;

/**
 * Client-side predicate: does `user` satisfy every active condition in `f`?
 * All conditions are AND-combined; empty fields are ignored.
 *
 * Semantics (from original userFilter.js):
 * - channelName: case-insensitive substring on profile fullname/name or primary channel title
 * - tags: comma-separated AND-list, case-insensitive substring per term
 * - country/language: comma-separated OR-list, exact code match (country upper-cased, language lower-cased)
 * - subscriberCount: "min~max" range on stats.allTime.subscribers
 * - recentGame: substring on recentGames[].title, optional "title, NN%" → percent >= NN
 * - recentGenre: substring on recentGenres[].title
 * - keywords/categories: comma-separated AND-list, case-insensitive substring per term
 * - email: case-insensitive substring on any resolved email (client-friendly; original was exact)
 * - inclusionComment: case-insensitive substring on inclusionComment or any campaigns[].comment
 */
export function matchesClientFilters(user: Influencer, f: ClientFilters, canSeeSuggested = false): boolean {
  // Channel name — case-insensitive substring on fullname/name/primary channel title
  const channelName = f.channelName.trim();
  if (channelName) {
    const q = channelName.toLowerCase();
    const ch = user.channels?.[0];
    const name = (user.profile?.fullname || user.profile?.name || ch?.title || '').toLowerCase();
    if (!name.includes(q)) return false;
  }

  // Tags — comma AND, substring per term
  const tags = f.tags.trim();
  if (tags) {
    const terms = tags.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
    const userTags = (user.tags ?? []).map((t) => t.toLowerCase());
    if (!terms.every((term) => userTags.some((t) => t.includes(term)))) return false;
  }

  // Country — comma OR, exact code (case-insensitive)
  const country = f.country.trim();
  if (country) {
    const codes = country.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean);
    const uc = (user.country || user.channels?.[0]?.country || '').toUpperCase();
    if (!codes.some((c) => uc === c)) return false;
  }

  // Language — comma OR, exact code (case-insensitive)
  const language = f.language.trim();
  if (language) {
    const codes = language.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
    const lng = (user.lng || '').toLowerCase();
    if (!codes.some((c) => lng === c)) return false;
  }

  // Subscribers — "min~max" range on stats.allTime.subscribers
  const subFilter = f.subscriberCount.trim();
  if (subFilter) {
    const [from, to] = parseRange(subFilter);
    const subs = user.stats?.allTime?.subscribers;
    if (typeof subs !== 'number') return false;
    if (from != null && subs < from) return false;
    if (to != null && subs > to) return false;
  }

  // Recent game — substring on title, optional min percent
  const recentGame = f.recentGame.trim();
  if (recentGame) {
    const { title, percent } = parseTitlePercent(recentGame);
    const q = title.toLowerCase();
    const games = user.recentGames ?? [];
    const ok = games.some((g) => titleOf(g).toLowerCase().includes(q) && (percent == null || percentOf(g) >= percent));
    if (!ok) return false;
  }

  // Recent genre — substring on title
  const recentGenre = f.recentGenre.trim();
  if (recentGenre) {
    const q = recentGenre.toLowerCase();
    const genres = user.recentGenres ?? [];
    if (!genres.some((g) => titleOf(g).toLowerCase().includes(q))) return false;
  }

  // Keywords — comma AND, substring per term
  const keywords = f.keywords.trim();
  if (keywords) {
    const terms = keywords.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
    const kws = (user.keywords ?? []).map((k) => k.toLowerCase());
    if (!terms.every((term) => kws.some((k) => k.includes(term)))) return false;
  }

  // Categories — comma AND, substring per term
  const categories = f.categories.trim();
  if (categories) {
    const terms = categories.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
    const cats = (user.categories ?? []).map((c) => c.toLowerCase());
    if (!terms.every((term) => cats.some((c) => c.includes(term)))) return false;
  }

  // Email — substring on any resolved email
  const email = f.email.trim();
  if (email) {
    const q = email.toLowerCase();
    const emails = getInfluencerEmails(user).map((e) => e.toLowerCase());
    if (!emails.some((e) => e.includes(q))) return false;
  }

  // Inclusion comment — substring on the default/suggested comment (only when the
  // viewer may see it) or any campaigns[].comment. The default lives in
  // `suggestedComment`; `inclusionComment` is a never-populated legacy fallback.
  const inclusionComment = f.inclusionComment.trim();
  if (inclusionComment) {
    const q = inclusionComment.toLowerCase();
    const suggested = canSeeSuggested ? (user.suggestedComment ?? user.inclusionComment ?? '') : '';
    const inc = suggested.toLowerCase();
    const inCampaigns = (user.campaigns ?? []).some((c) => (c.comment || '').toLowerCase().includes(q));
    if (!inc.includes(q) && !inCampaigns) return false;
  }

  return true;
}

